import { escapeQuery } from './search';

export const getLabelValue = (
  labels: { key: string; value?: string | null }[],
  key: string,
) => {
  return labels.find(l => l.key === key)?.value ?? '';
};

export const mergeLabelsIntoCsv = (csv: string, valueToAdd: string) => {
  const set = new Set(
    csv
      .split(',')
      .map(s => s.trim())
      .filter(Boolean),
  );
  if (valueToAdd.trim()) set.add(valueToAdd.trim());
  return [...set].join(',');
};

export const replaceLabel = (
  labels: { key: string; value?: string | null }[],
  key: string,
  value: string,
) => {
  return [
    ...labels
      .filter(l => l.key !== key)
      .map(l => ({ key: l.key, value: l.value ?? '' })),
    { key, value },
  ];
};

export const removeValueFromCsv = (csv: string, valueToRemove: string) => {
  const target = valueToRemove.trim();
  if (!csv.trim() || !target) return csv.trim();

  const values = csv
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

  const next = values.filter(v => v !== target);
  return next.join(',');
};

export const csvIncludes = (csv: string, value: string) => {
  const target = value.trim();
  if (!target) return false;
  return csv
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .includes(target);
};

export const labelCsvHasValue = (
  labels: { key: string; value?: string | null }[],
  key: string,
  value: string,
) => {
  const csv = getLabelValue(labels, key);
  return csvIncludes(csv, value);
};

// Builds a precise CSV token filter for one or more values against a label key.
// It matches exact tokens in any position within the CSV: first, middle, last, or the only token.
export const buildCsvLabelExactTokenFilter = (
  values: string[],
  key: string,
) => {
  const field = `labels.${key}`;
  const parts = (values ?? [])
    .map(v => (v ?? '').trim())
    .filter(Boolean)
    .map(v => {
      const escaped = escapeQuery(v);
      const first = `${field}:"${escaped},*"`;
      const middle = `${field}:"*,${escaped},*"`;
      const last = `${field}:"*,${escaped}"`;
      const exact = `${field}:"${escaped}"`;
      return `(${first} OR ${middle} OR ${last} OR ${exact})`;
    });
  return parts.join(' OR ');
};

export const strictlyFilteredList = <
  T extends { labels?: { key: string; value?: string | null }[] },
>(
  items: T[] | undefined,
  labelKey: string,
  values: string[],
) => {
  const trimmedValues = (values ?? [])
    .map(v => (v ?? '').trim())
    .filter(Boolean);
  if (!trimmedValues.length) return items;

  return (items ?? []).filter(item =>
    trimmedValues.some(v => labelCsvHasValue(item.labels ?? [], labelKey, v)),
  );
};
